/**
 * Official CTD/IND section headings (FDA Module 1 TOC + ICH CTD).
 * Static regulatory data — mirrors backend OFFICIAL_HEADINGS.
 * Used by SectionPicker for instant client-side filtering.
 */
export const CTD_SECTIONS: Record<string, string> = {
  // Module 1
  "1.1": "Forms",
  "1.2": "Cover Letters",
  "1.3": "Administrative Information",
  "1.3.1": "Contact/Sponsor/Applicant Information",
  "1.4": "References",
  "1.5": "Withdrawal of Listed Drug",
  "1.6": "Meetings",
  "1.6.1": "Meeting Request",
  "1.6.2": "Meeting Background Materials",
  "1.7": "Fast Track",
  "1.7.1": "Fast Track Designation Request",
  "1.8": "Special Protocol Assessment Request",
  "1.9": "Pediatric Administrative Information",
  "1.9.1": "Request for Waiver of Pediatric Studies",
  "1.10": "Dispute Resolution",
  "1.11": "Information Amendment",
  "1.11.1": "Quality Information Amendment",
  "1.11.2": "Nonclinical Information Amendment",
  "1.11.3": "Clinical Information Amendment",
  "1.11.4": "Multiple Module Information Amendment",
  "1.12": "Other Correspondence",
  "1.12.1": "Pre-IND Correspondence",
  "1.12.1.4": "Roadmap of Responses from Pre-IND Meeting",
  "1.12.13": "Request for Waiver for In Vivo Studies",
  "1.13": "Annual Report",
  "1.13.15": "Development Safety Update Report (DSUR)",
  "1.14": "Labeling",
  "1.14.1": "Draft Labeling",
  "1.14.4": "Investigational Drug Labeling",
  "1.14.4.1": "Investigator Brochure",
  "1.14.4.2": "Investigational Drug Labeling",
  "1.15": "Promotional Material",
  "1.16": "Risk Management Plan",
  "1.17": "Postmarketing Studies",
  "1.18": "Proprietary Names",
  "1.19": "Pre-EUA and EUA",
  "1.20": "General Investigational Plan",
  // Module 2
  "2.1": "CTD Table of Contents",
  "2.2": "CTD Introduction",
  "2.3": "Quality Overall Summary",
  "2.3.S": "Drug Substance",
  "2.3.P": "Drug Product",
  "2.4": "Nonclinical Overview",
  "2.5": "Clinical Overview",
  "2.6": "Nonclinical Written and Tabulated Summaries",
  "2.6.1": "Introduction",
  "2.6.2": "Pharmacology Written Summary",
  "2.6.3": "Pharmacokinetics Written Summary",
  "2.6.4": "Toxicology Written Summary",
  "2.7": "Clinical Summary",
  "2.7.1": "Summary of Biopharmaceutic Studies",
  "2.7.2": "Summary of Clinical Pharmacology Studies",
  "2.7.3": "Summary of Clinical Efficacy",
  "2.7.4": "Summary of Clinical Safety",
  "2.7.5": "References",
  "2.7.6": "Synopses of Individual Studies",
  // Module 3
  "3.2": "Body of Data",
  "3.2.S": "Drug Substance",
  "3.2.S.1": "General Information",
  "3.2.S.1.1": "Nomenclature",
  "3.2.S.1.2": "Structure",
  "3.2.S.1.3": "General Properties",
  "3.2.S.2": "Manufacture",
  "3.2.S.3": "Characterisation",
  "3.2.S.4": "Control of Drug Substance",
  "3.2.S.4.1": "Specification",
  "3.2.S.4.2": "Analytical Procedures",
  "3.2.S.4.3": "Validation of Analytical Procedures",
  "3.2.S.4.4": "Batch Analyses",
  "3.2.S.5": "Reference Standards or Materials",
  "3.2.S.6": "Container Closure System",
  "3.2.S.7": "Stability",
  "3.2.P": "Drug Product",
  "3.2.P.1": "Description and Composition",
  "3.2.P.2": "Pharmaceutical Development",
  "3.2.P.3": "Manufacture",
  "3.2.P.4": "Control of Excipients",
  "3.2.P.5": "Control of Drug Product",
  "3.2.P.6": "Reference Standards or Materials",
  "3.2.P.7": "Container Closure System",
  "3.2.P.8": "Stability",
  "3.2.A": "Appendices",
  "3.2.A.1": "Facilities and Equipment",
  "3.2.A.2": "Adventitious Agents Safety Evaluation",
  "3.2.A.3": "Excipients",
  "3.2.R": "Regional Information",
  // Module 4
  "4.1": "Table of Contents",
  "4.2": "Study Reports",
  "4.2.1": "Pharmacology",
  "4.2.1.1": "Primary Pharmacodynamics",
  "4.2.1.2": "Secondary Pharmacodynamics",
  "4.2.1.3": "Safety Pharmacology",
  "4.2.2": "Pharmacokinetics",
  "4.2.3": "Toxicology",
  "4.2.3.1": "Single-Dose Toxicity",
  "4.2.3.2": "Repeat-Dose Toxicity",
  "4.2.3.3": "Genotoxicity",
  "4.2.3.4": "Carcinogenicity",
  "4.2.3.5": "Reproductive and Developmental Toxicity",
  "4.2.3.7": "Other Toxicity Studies",
  "4.3": "Literature References",
  // Module 5
  "5.1": "Table of Contents",
  "5.2": "Tabular Listing of All Clinical Studies",
  "5.3": "Clinical Study Reports",
  "5.3.1": "Biopharmaceutic Studies",
  "5.3.1.1": "Bioavailability Study Reports",
  "5.3.2": "Studies Using Human Biomaterials",
  "5.3.3": "Human Pharmacokinetic Studies",
  "5.3.3.1": "Healthy Subject PK Studies",
  "5.3.4": "Human Pharmacodynamic Studies",
  "5.3.5": "Efficacy and Safety Studies",
  "5.3.5.1": "Controlled Clinical Studies",
  "5.3.5.2": "Uncontrolled Clinical Studies",
  "5.3.5.3": "Analyses of Data from More Than One Study",
  "5.3.5.4": "Other Study Reports",
  "5.3.6": "Postmarketing Experience",
  "5.3.7": "Case Report Forms",
  "5.4": "Literature References",
};

/** Pre-sorted list of entries for SectionPicker. */
export const CTD_SECTION_LIST = Object.entries(CTD_SECTIONS).map(
  ([section, title]) => ({ section, title })
);
